import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { 
  Home, 
  Mail, 
  Eye, 
  Users, 
  Zap, 
  TrendingUp, 
  Monitor, 
  Smartphone,
  CheckCircle,
  Sparkles,
  Trophy,
  Play,
  BarChart3,
  Globe,
  Clock,
  ArrowRight,
  Scale,
  Gavel,
  Shield,
  DollarSign,
  Receipt,
  Calendar,
  Wallet,
  LifeBuoy
} from 'lucide-react';
import BirdMascot from '../components/BirdMascot';

const ADS_HERO = "https://static.prod-images.emergentagent.com/jobs/2904d8ca-0a1a-4bb5-9d6b-5743d189e642/images/a9bde93a6284e19ad1e3c9da97ed9cae54fa21d90b1b79e65b7739ad1b3e0038.jpeg";

const AdPlacement = ({ title, description, icon: Icon, position, highlight, delay }) => (
  <motion.div
    initial={{ opacity: 0, y: 30 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay, duration: 0.5 }}
  >
    <Card className={`p-6 h-full bg-gradient-to-br ${highlight ? 'from-primary/20 via-primary/10 to-transparent border-primary/40' : 'from-card to-muted/20 border-border'} hover:border-primary/50 transition-all duration-300 hover:shadow-lg hover:shadow-primary/10`}>
      <div className={`w-14 h-14 rounded-2xl ${highlight ? 'bg-primary/20' : 'bg-muted'} flex items-center justify-center mb-4`}>
        <Icon className={`w-7 h-7 ${highlight ? 'text-primary' : 'text-muted-foreground'}`} />
      </div>
      <div className="text-xs font-semibold text-primary uppercase tracking-wider mb-2">
        {position}
      </div>
      <h3 className="text-xl font-heading font-bold mb-2">{title}</h3>
      <p className="text-muted-foreground text-sm leading-relaxed">{description}</p>
    </Card>
  </motion.div>
);

const StatCard = ({ value, label, icon: Icon, delay }) => (
  <motion.div
    initial={{ opacity: 0, scale: 0.9 }}
    animate={{ opacity: 1, scale: 1 }}
    transition={{ delay, duration: 0.4 }}
    className="text-center p-6"
  >
    <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-3">
      <Icon className="w-6 h-6 text-primary" />
    </div>
    <div className="text-3xl sm:text-4xl font-heading font-black text-gradient-primary mb-1">
      {value}
    </div>
    <div className="text-sm text-muted-foreground">{label}</div>
  </motion.div>
);

const BenefitItem = ({ text, delay }) => (
  <motion.div
    initial={{ opacity: 0, x: -20 }}
    animate={{ opacity: 1, x: 0 }}
    transition={{ delay, duration: 0.4 }}
    className="flex items-start gap-3"
  >
    <CheckCircle className="w-5 h-5 text-primary shrink-0 mt-0.5" />
    <span className="text-muted-foreground">{text}</span>
  </motion.div>
);

const AdvertisePage = () => {
  const navigate = useNavigate();

  const adPlacements = [
    {
      title: "Homepage Banner",
      description: "Prime real estate on our landing page. Every visitor sees this ad before they start playing. Maximum brand exposure with high-impact positioning.",
      icon: Monitor,
      position: "Placement #1",
      highlight: false
    },
    {
      title: "In-Game Interstitials",
      description: "Two full-screen takeovers during gameplay. Captive audience with 5-second guaranteed view time each. Impossible to miss.",
      icon: Zap,
      position: "Placement #2 & #3",
      highlight: true
    },
    {
      title: "Leaderboard Sidebar",
      description: "Displayed alongside our competitive leaderboard. Reaches players checking scores and tracking progress. High repeat views.",
      icon: Trophy,
      position: "Placement #4",
      highlight: false
    }
  ];

  const benefits = [
    "100% viewability on interstitial ads",
    "Engaged gaming audience that pays attention",
    "Multiple touchpoints per session",
    "Mobile and desktop optimized placements",
    "Flexible ad formats (image, video, animated)",
    "Competitive pricing with package deals",
    "Detailed performance analytics",
    "Direct partnership opportunities"
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="border-b border-border bg-card/50 backdrop-blur-sm sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Button
              variant="ghost"
              onClick={() => navigate('/')}
              className="text-muted-foreground hover:text-foreground"
            >
              <Home className="w-4 h-4 mr-2" />
              Home
            </Button>
            <Button
              onClick={() => window.location.href = 'mailto:support@jailbirdsgame.com?subject=Advertising%20Inquiry'}
              className="bg-gradient-primary hover-glow-primary text-primary-foreground font-semibold"
            >
              <Mail className="w-4 h-4 mr-2" />
              Contact Us
            </Button>
          </div>
        </div>
      </div>

      {/* Hero Section */}
      <section className="relative overflow-hidden py-16 sm:py-24">
        <div className="absolute inset-0 bg-gradient-to-b from-primary/5 via-transparent to-transparent" />
        <div className="absolute top-20 left-10 w-72 h-72 bg-primary/10 rounded-full blur-3xl" />
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-accent/10 rounded-full blur-3xl" />
        
        <div className="container mx-auto px-4 relative">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center max-w-4xl mx-auto"
          >
            <div className="flex justify-center gap-4 mb-6">
              <motion.div
                animate={{ y: [0, -10, 0] }}
                transition={{ duration: 2, repeat: Infinity }}
              >
                <BirdMascot size={60} color="#10B981" />
              </motion.div>
              <motion.div
                animate={{ y: [0, -10, 0] }}
                transition={{ duration: 2, repeat: Infinity, delay: 0.3 }}
              >
                <BirdMascot size={60} color="#FF6B6B" />
              </motion.div>
              <motion.div
                animate={{ y: [0, -10, 0] }}
                transition={{ duration: 2, repeat: Infinity, delay: 0.6 }}
              >
                <BirdMascot size={60} color="#8B5CF6" />
              </motion.div>
            </div>
            
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-black mb-6">
              Advertise With{' '}
              <span className="text-gradient-primary">JailBirds</span>
            </h1>
            
            <p className="text-lg sm:text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
              The most precisely targeted place to reach people during and after an arrest.
              Perfect for <span className="text-foreground font-semibold">bail bonds companies, criminal defense &amp; DUI attorneys, expungement and recovery services</span> — your brand in front of a local, high-intent audience.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                size="lg"
                onClick={() => window.location.href = 'mailto:support@jailbirdsgame.com?subject=Advertising%20Inquiry'}
                className="bg-gradient-primary hover-glow-primary text-primary-foreground font-bold text-lg px-8"
              >
                <Mail className="w-5 h-5 mr-2" />
                Get Started
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
              <Button
                size="lg"
                variant="outline"
                onClick={() => navigate('/game')}
                className="font-semibold text-lg px-8"
              >
                <Play className="w-5 h-5 mr-2" />
                See Ads in Action
              </Button>
            </div>

            {/* Showcase graphic */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.6 }}
              className="mt-14 max-w-3xl mx-auto"
            >
              <div className="relative rounded-2xl overflow-hidden border border-primary/30 shadow-2xl shadow-primary/10">
                <img src={ADS_HERO} alt="Your ad, in the game" className="w-full h-auto" loading="lazy" />
                <div className="absolute top-3 right-3 bg-primary/90 text-primary-foreground text-xs font-bold px-3 py-1 rounded-full">
                  YOUR AD HERE
                </div>
              </div>
              <p className="text-sm text-muted-foreground mt-3">
                Full-screen, sound-optional video &amp; banner placements — seen at peak attention.
              </p>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-12 border-y border-border bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <StatCard value="$0.09" label="Per Ad Serve" icon={DollarSign} delay={0.1} />
            <StatCard value="100%" label="Viewability" icon={Eye} delay={0.2} />
            <StatCard value="5s+" label="Guaranteed Views" icon={Clock} delay={0.3} />
            <StatCard value="Daily" label="Local Mecklenburg Reach" icon={Globe} delay={0.4} />
          </div>
        </div>
      </section>

      {/* Ad Placements Section */}
      <section className="py-16 sm:py-24">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl sm:text-4xl font-heading font-bold mb-4">
              <span className="text-gradient-primary">3 Premium</span> Ad Placements
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Every game session includes three strategic ad placements, ensuring your brand 
              gets maximum exposure to our engaged player base.
            </p>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {adPlacements.map((placement, index) => (
              <AdPlacement key={index} {...placement} delay={0.1 * (index + 1)} />
            ))}
          </div>
        </div>
      </section>

      {/* Why Advertise Section */}
      <section className="py-16 sm:py-24 bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
            >
              <h2 className="text-3xl sm:text-4xl font-heading font-bold mb-6">
                Why Advertise With{' '}
                <span className="text-gradient-primary">JailBirds?</span>
              </h2>
              <p className="text-muted-foreground mb-8 text-lg">
                Our players are engaged, attentive, and spend quality time with our game. 
                Unlike passive scrolling, gaming audiences actively focus on the screen—making 
                your ads impossible to ignore.
              </p>
              
              <div className="space-y-4">
                {benefits.map((benefit, index) => (
                  <BenefitItem key={index} text={benefit} delay={0.1 * (index + 1)} />
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <Card className="p-8 bg-gradient-to-br from-primary/10 via-card to-card border-primary/30">
                <div className="text-center mb-6">
                  <div className="w-16 h-16 rounded-full bg-primary/20 flex items-center justify-center mx-auto mb-4">
                    <TrendingUp className="w-8 h-8 text-primary" />
                  </div>
                  <h3 className="text-2xl font-heading font-bold mb-2">
                    Interstitial Ads
                  </h3>
                  <p className="text-muted-foreground">
                    Our most premium placement
                  </p>
                </div>

                <div className="space-y-4 mb-6">
                  <div className="flex items-center justify-between p-3 rounded-lg bg-background/50">
                    <span className="text-sm text-muted-foreground">Format</span>
                    <span className="font-semibold">Full-Screen Takeover</span>
                  </div>
                  <div className="flex items-center justify-between p-3 rounded-lg bg-background/50">
                    <span className="text-sm text-muted-foreground">Duration</span>
                    <span className="font-semibold">5+ Seconds</span>
                  </div>
                  <div className="flex items-center justify-between p-3 rounded-lg bg-background/50">
                    <span className="text-sm text-muted-foreground">Frequency</span>
                    <span className="font-semibold">2x Per Game</span>
                  </div>
                  <div className="flex items-center justify-between p-3 rounded-lg bg-background/50">
                    <span className="text-sm text-muted-foreground">Viewability</span>
                    <span className="font-semibold text-primary">100%</span>
                  </div>
                </div>

                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Smartphone className="w-4 h-4" />
                  <span>Optimized for mobile & desktop</span>
                </div>
              </Card>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Ad Formats Section */}
      <section className="py-16 sm:py-24">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl sm:text-4xl font-heading font-bold mb-4">
              Flexible <span className="text-gradient-primary">Ad Formats</span>
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              We support multiple creative formats to match your campaign needs
            </p>
          </motion.div>

          <div className="grid sm:grid-cols-3 gap-6 max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
            >
              <Card className="p-6 text-center hover:border-primary/30 transition-colors">
                <div className="w-12 h-12 rounded-xl bg-blue-500/10 flex items-center justify-center mx-auto mb-4">
                  <Monitor className="w-6 h-6 text-blue-500" />
                </div>
                <h3 className="font-heading font-bold mb-2">Static Images</h3>
                <p className="text-sm text-muted-foreground">
                  PNG, JPG, WebP<br />
                  High-quality banners
                </p>
              </Card>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              <Card className="p-6 text-center hover:border-primary/30 transition-colors">
                <div className="w-12 h-12 rounded-xl bg-purple-500/10 flex items-center justify-center mx-auto mb-4">
                  <Sparkles className="w-6 h-6 text-purple-500" />
                </div>
                <h3 className="font-heading font-bold mb-2">Animated</h3>
                <p className="text-sm text-muted-foreground">
                  GIF, Animated WebP<br />
                  Eye-catching motion
                </p>
              </Card>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
            >
              <Card className="p-6 text-center hover:border-primary/30 transition-colors">
                <div className="w-12 h-12 rounded-xl bg-red-500/10 flex items-center justify-center mx-auto mb-4">
                  <Play className="w-6 h-6 text-red-500" />
                </div>
                <h3 className="font-heading font-bold mb-2">Video</h3>
                <p className="text-sm text-muted-foreground">
                  MP4, WebM<br />
                  Maximum engagement
                </p>
              </Card>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Who Should Advertise */}
      <section className="py-16 sm:py-24 bg-muted/30">
        <div className="container mx-auto px-4">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-heading font-bold mb-4">
              Built for <span className="text-gradient-primary">Your Industry</span>
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Our players are face-to-face with real local arrest records — the exact moment your services become relevant. No wasted spend on the wrong audience.
            </p>
          </motion.div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 max-w-5xl mx-auto">
            {[
              { icon: Shield, title: 'Bail Bonds Companies', desc: 'Reach people the moment bail is top of mind.' },
              { icon: Gavel, title: 'Criminal Defense Attorneys', desc: 'Be the name they remember when they need representation.' },
              { icon: Scale, title: 'DUI & Traffic Lawyers', desc: 'Connect with an audience surrounded by charge-related content.' },
              { icon: LifeBuoy, title: 'Expungement & Recovery', desc: 'Support services people seek right after a booking.' },
            ].map((a, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.08 * i }}>
                <Card className="p-6 h-full text-center bg-gradient-to-br from-card to-muted/20 border-border hover:border-primary/40 transition-all">
                  <div className="w-14 h-14 rounded-2xl bg-primary/15 flex items-center justify-center mx-auto mb-4">
                    <a.icon className="w-7 h-7 text-primary" />
                  </div>
                  <h3 className="font-heading font-bold mb-2">{a.title}</h3>
                  <p className="text-sm text-muted-foreground">{a.desc}</p>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Transparent Pricing */}
      <section className="py-16 sm:py-24">
        <div className="container mx-auto px-4">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-heading font-bold mb-4">
              Simple, <span className="text-gradient-primary">Transparent Pricing</span>
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              One flat rate for every time your ad is actually shown to a player. No vague packages, no paying for impressions that never rendered.
            </p>
          </motion.div>

          <div className="grid lg:grid-cols-2 gap-8 max-w-5xl mx-auto items-stretch">
            {/* Rate card */}
            <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
              <Card className="p-8 h-full bg-gradient-to-br from-primary/15 via-card to-card border-primary/40 text-center flex flex-col justify-center">
                <div className="text-sm uppercase tracking-widest text-muted-foreground mb-2">Flat rate per ad serve</div>
                <div className="text-6xl font-heading font-black text-gradient-primary mb-2">$0.09</div>
                <p className="text-muted-foreground mb-6">You only pay when your ad is displayed. Set a monthly budget cap and we'll never exceed it.</p>
                <div className="flex items-center justify-center gap-2 text-sm text-primary font-semibold">
                  <CheckCircle className="w-4 h-4" /> Pay only for real serves
                </div>
              </Card>
            </motion.div>

            {/* Example table */}
            <motion.div initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
              <Card className="p-8 h-full bg-card border-border">
                <h3 className="font-heading font-bold mb-4 flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-primary" /> What it costs
                </h3>
                <div className="space-y-3">
                  {[['10,000 serves', '$240'], ['25,000 serves', '$600'], ['50,000 serves', '$1,200']].map(([serves, cost], i) => {
                    const pct = [40, 70, 100][i];
                    return (
                      <div key={serves}>
                        <div className="flex items-center justify-between text-sm mb-1">
                          <span className="text-muted-foreground">{serves}</span>
                          <span className="font-bold text-primary">{cost}</span>
                        </div>
                        <div className="h-2.5 rounded-full bg-muted overflow-hidden">
                          <div className="h-full rounded-full bg-gradient-primary" style={{ width: `${pct}%` }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
                <p className="text-xs text-muted-foreground mt-5">
                  Because context does the targeting, every dollar works harder than broad social or search spend.
                </p>
              </Card>
            </motion.div>
          </div>
        </div>
      </section>

      {/* How Billing Works */}
      <section className="py-16 sm:py-24 bg-muted/30">
        <div className="container mx-auto px-4">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-heading font-bold mb-4">
              How <span className="text-gradient-primary">Billing Works</span>
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Completely transparent — you always know exactly what you paid for.
            </p>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-5 max-w-6xl mx-auto">
            {[
              { icon: Eye, title: 'We track every serve', desc: 'Each display is logged in real time with date, placement and unique-visitor count.' },
              { icon: BarChart3, title: 'Live dashboard', desc: 'Watch serves, unique reach, clicks and running spend update daily.' },
              { icon: Receipt, title: 'Monthly invoice', desc: 'At month-end we total serves × rate and send an itemized, per-ad breakdown.' },
              { icon: Wallet, title: 'Budget caps', desc: 'Set a ceiling — billing stops and ads pause the moment it\u2019s reached.' },
              { icon: Calendar, title: 'Net-15 terms', desc: 'Standard business payment methods. No contract, no setup fees.' },
            ].map((s, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.07 * i }}>
                <Card className="p-6 h-full bg-card border-border relative">
                  <div className="absolute -top-3 -left-3 w-8 h-8 rounded-full bg-gradient-primary text-primary-foreground text-sm font-bold flex items-center justify-center shadow-lg">
                    {i + 1}
                  </div>
                  <div className="w-11 h-11 rounded-xl bg-primary/15 flex items-center justify-center mb-3">
                    <s.icon className="w-6 h-6 text-primary" />
                  </div>
                  <h3 className="font-heading font-bold text-sm mb-1.5">{s.title}</h3>
                  <p className="text-xs text-muted-foreground leading-relaxed">{s.desc}</p>
                </Card>
              </motion.div>
            ))}
          </div>
          <p className="text-center text-sm text-muted-foreground mt-8 max-w-2xl mx-auto">
            Your invoice equals <span className="text-foreground font-semibold">serves × $0.09</span> — nothing more. We report unique viewers alongside raw serves, so you know how many distinct local people your brand actually reached.
          </p>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 sm:py-24 bg-gradient-to-br from-primary/10 via-background to-accent/10">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center max-w-3xl mx-auto"
          >
            <div className="w-20 h-20 rounded-full bg-primary/20 flex items-center justify-center mx-auto mb-6">
              <Mail className="w-10 h-10 text-primary" />
            </div>
            
            <h2 className="text-3xl sm:text-4xl font-heading font-bold mb-4">
              Ready to Reach Our Audience?
            </h2>
            <p className="text-lg text-muted-foreground mb-8">
              Get in touch to discuss rates, availability, and custom packages. 
              We'll help you create an advertising strategy that delivers results.
            </p>

            <Card className="p-8 bg-card/80 backdrop-blur-sm border-primary/20 max-w-md mx-auto mb-8">
              <div className="flex items-center justify-center gap-3 mb-4">
                <Mail className="w-6 h-6 text-primary" />
                <span className="text-xl font-heading font-bold">Contact Us</span>
              </div>
              <a 
                href="mailto:support@jailbirdsgame.com?subject=Advertising%20Inquiry"
                className="text-2xl font-bold text-primary hover:underline"
              >
                support@jailbirdsgame.com
              </a>
              <p className="text-sm text-muted-foreground mt-3">
                We typically respond within 24 hours
              </p>
            </Card>

            <Button
              size="lg"
              onClick={() => window.location.href = 'mailto:support@jailbirdsgame.com?subject=Advertising%20Inquiry'}
              className="bg-gradient-primary hover-glow-primary text-primary-foreground font-bold text-lg px-10"
            >
              <Mail className="w-5 h-5 mr-2" />
              Send Inquiry
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-border bg-card/50 py-8">
        <div className="container mx-auto px-4">
          <div className="flex flex-col items-center gap-4">
            <div className="flex flex-wrap justify-center gap-4 text-sm">
              <a href="/" className="text-primary hover:underline font-medium">
                Home
              </a>
              <span className="text-muted-foreground">•</span>
              <a href="/about" className="text-primary hover:underline font-medium">
                About
              </a>
              <span className="text-muted-foreground">•</span>
              <a href="/legal" className="text-primary hover:underline font-medium">
                Privacy & Legal
              </a>
              <span className="text-muted-foreground">•</span>
              <a href="mailto:support@jailbirdsgame.com" className="text-primary hover:underline font-medium">
                Contact
              </a>
            </div>
            <p className="text-sm text-muted-foreground text-center">
              © 2025 JailBirds. Owned and operated by M.Panepinto. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default AdvertisePage;
